// Workflow Hygiene group: wraps the three governance cards in a single
// red-bordered region at the top of the Product Flow tab. Per AC2.1.2 the
// group only renders when scope is a single team — when filters.teamId is
// empty (All Teams / All Products / region or product-level scope) the
// component returns null and no API request is made.
//
// Single round-trip via portfolioApi.getWorkflowHygiene; the cards
// themselves are dumb presentation components.

import { useMemo } from 'react';
import { ErrorBanner, Spinner } from '@/components/ui';
import { useApi } from '@/hooks/useApi';
import type { IMetricViewFilters } from '@/hooks/use-metric-view-filters';
import { portfolioApi } from '@/services/portfolio-api';
import { buildBucketAxisProps, todayIsoUtc } from '@/lib/metric-view/bucket-format';
import { EpicLinkageCard } from './EpicLinkageCard';
import { StatusCountCard } from './StatusCountCard';
import { IssueTypeComplianceCard } from './IssueTypeComplianceCard';

interface WorkflowHygieneGroupProps {
  filters: IMetricViewFilters;
}

/**
 * Per AC2.1.2 the cards only render when scope is a single team. The early
 * return for `!teamId` lives BEFORE any hooks so we don't fire the
 * workflow-hygiene fetch for All Teams / All Products / region scopes.
 * The actual data fetch is delegated to an inner component which only
 * mounts when teamId is set — keeping the rules-of-hooks invariant intact.
 */
export function WorkflowHygieneGroup({ filters }: Readonly<WorkflowHygieneGroupProps>) {
  if (!filters.teamId) return null;
  return <WorkflowHygieneGroupContent filters={filters} />;
}

function WorkflowHygieneGroupContent({ filters }: Readonly<WorkflowHygieneGroupProps>) {
  const { data, loading, error } = useApi(
    () => portfolioApi.getWorkflowHygiene({
      from: filters.from,
      to: filters.to,
      teamId: filters.teamId,
      productId: filters.productId || undefined,
    }),
    [filters.teamId, filters.from, filters.to, filters.productId],
  );
  const axis = useMemo(
    () => buildBucketAxisProps({
      today: todayIsoUtc(),
      rangeFrom: filters.from,
      rangeTo: filters.to,
    }),
    [filters.from, filters.to],
  );

  return (
    <section
      aria-labelledby="workflow-hygiene-heading"
      className="rounded-lg border-2 border-error p-4"
    >
      <h2
        id="workflow-hygiene-heading"
        className="mb-3 inline-block rounded-sm bg-error px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-error-foreground"
      >
        Workflow Hygiene
      </h2>

      {loading && (
        <div className="flex h-32 items-center justify-center">
          <Spinner />
        </div>
      )}

      {!loading && error && <ErrorBanner message={error} />}

      {!loading && !error && data && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <EpicLinkageCard data={data.epicLinkage} axis={axis} />
          <StatusCountCard data={data.statusCount} syncHealth={data.syncHealth} />
          <IssueTypeComplianceCard data={data.issueTypeCompliance} />
        </div>
      )}
    </section>
  );
}
